import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, documentsAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Search, Download, Eye, EyeOff, FileText, Upload, X, List, Trash2, AlertTriangle } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  PENDENTE: 'badge-neutral', PROTOCOLAR: 'badge-warning', ENVIADO: 'badge-warning',
  PROTOCOLADO: 'badge-success', BAIXA_PARCIAL: 'badge-warning', FINALIZADO: 'badge-success',
};
const STATUS_LABEL = {
  PENDENTE: 'Pendente', PROTOCOLAR: 'Protocolar', ENVIADO: 'Enviado',
  PROTOCOLADO: 'Protocolado', BAIXA_PARCIAL: 'Baixa Parcial', FINALIZADO: 'Finalizado',
};
const DOC_BADGE = {
  validado: 'badge-success', atencao: 'badge-warning', recebido: 'badge-warning',
  divergente: 'badge-danger', approved: 'badge-success', '—': 'badge-neutral',
};
const DOC_LABEL = {
  validado: 'Validado', atencao: 'Recebido', recebido: 'Recebido',
  divergente: 'Divergente', approved: 'Validado', '—': '—',
};

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '' });
  const [readyFilter, setReadyFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState([]);
  const [exporting, setExporting] = useState(false);
  // Preview modal
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');
  // Serasa upload
  const [serasaTarget, setSerasaTarget] = useState(null);
  const serasaRef = useRef(null);
  const navigate = useNavigate();

  const fetchClients = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.listClients({ page, search, limit: 25, ready: readyFilter, statusFilter });
      setClients(res.data.clients);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch { toast.error('Erro ao carregar'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchClients(); }, [page, search, readyFilter, statusFilter]);

  const handleCreate = async (e) => {
    e.preventDefault(); setCreating(true);
    try { await adminAPI.createClient(form); toast.success('Cliente criado!'); setShowCreate(false); setForm({name:'',email:'',password:'',phone:''}); fetchClients(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
    finally { setCreating(false); }
  };

  const toggleSelect = (id) => setSelected(p => p.includes(id) ? p.filter(x=>x!==id) : [...p, id]);
  const toggleAll = () => selected.length === clients.length ? setSelected([]) : setSelected(clients.map(c=>c.id));

  const handleExport = async () => {
    if (!selected.length) return;
    setExporting(true);
    try {
      const res = await adminAPI.exportClients(selected);
      const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([res.data]));
      a.download = 'clientes_recupnet.xlsx'; document.body.appendChild(a); a.click(); a.remove();
      toast.success(`${selected.length} exportado(s)`);
    } catch { toast.error('Erro ao exportar'); }
    finally { setExporting(false); }
  };

  const handleGenerateList = async () => {
    if (!selected.length) return;
    try {
      // Get case IDs for selected clients
      const caseIds = clients.filter(c => selected.includes(c.id) && c.caseId).map(c => c.caseId);
      await adminAPI.createExportList({ name: `Lista ${new Date().toLocaleDateString('pt-BR')}`, associationName: 'Associacao', caseIds });
      toast.success('Lista criada! Acesse a aba Listas.');
      setSelected([]);
      fetchClients();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao criar lista'); }
  };

  const openPreview = (url, title) => { setPreviewUrl(url); setPreviewTitle(title); };
  const closePreview = () => { setPreviewUrl(null); setPreviewTitle(''); };

  const handleSerasaUpload = async (clientId, file) => {
    try {
      await adminAPI.uploadSerasa(clientId, file);
      toast.success('Relatorio Serasa anexado!');
      fetchClients();
    } catch { toast.error('Erro ao anexar'); }
    setSerasaTarget(null);
  };

  // Protocolo modal
  const [protoModal, setProtoModal] = useState(null); // {caseId, nome} or 'lote'
  const [protoForm, setProtoForm] = useState({ numeroProtocolo: '', observacao: '' });
  const [protoLoading, setProtoLoading] = useState(false);

  const handleProtocolarIndividual = async () => {
    if (!protoModal?.caseId || !protoForm.numeroProtocolo) { toast.error('Numero do protocolo obrigatorio'); return; }
    setProtoLoading(true);
    try {
      await adminAPI.protocolarIndividual({ caseId: protoModal.caseId, ...protoForm });
      toast.success('Acao protocolada!');
      setProtoModal(null); setProtoForm({ numeroProtocolo: '', observacao: '' });
      fetchClients();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
    finally { setProtoLoading(false); }
  };

  const handleProtocolarLote = async () => {
    if (!selected.length || !protoForm.numeroProtocolo) { toast.error('Selecione clientes e informe protocolo'); return; }
    const caseIds = clients.filter(c => selected.includes(c.id) && c.caseId).map(c => c.caseId);
    setProtoLoading(true);
    try {
      await adminAPI.protocolarLote({ caseIds, ...protoForm });
      toast.success(`${caseIds.length} acoes protocoladas!`);
      setProtoModal(null); setProtoForm({ numeroProtocolo: '', observacao: '' }); setSelected([]);
      fetchClients();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
    finally { setProtoLoading(false); }
  };

  // Baixa modal
  const [baixaModal, setBaixaModal] = useState(null); // {caseId, nome} or 'lote'
  const [baixaForm, setBaixaForm] = useState({ empresa: 'SPC', observacao: '' });
  const [baixaLoading, setBaixaLoading] = useState(false);
  const EMPRESAS = ['SPC', 'SERASA', 'BOA_VISTA', 'CENPROT'];

  const handleBaixaIndividual = async () => {
    if (!baixaModal?.caseId) return;
    setBaixaLoading(true);
    try {
      await adminAPI.baixaIndividual({ caseId: baixaModal.caseId, ...baixaForm });
      toast.success(`Baixa ${baixaForm.empresa} registrada!`);
      setBaixaModal(null); setBaixaForm({ empresa: 'SPC', observacao: '' });
      fetchClients();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
    finally { setBaixaLoading(false); }
  };

  const handleBaixaLote = async () => {
    if (!selected.length) return;
    const caseIds = clients.filter(c => selected.includes(c.id) && c.caseId).map(c => c.caseId);
    setBaixaLoading(true);
    try {
      await adminAPI.baixaLote({ caseIds, ...baixaForm });
      toast.success(`Baixa ${baixaForm.empresa} para ${caseIds.length} clientes!`);
      setBaixaModal(null); setBaixaForm({ empresa: 'SPC', observacao: '' }); setSelected([]);
      fetchClients();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
    finally { setBaixaLoading(false); }
  };

  // Delete modal state
  const [deleteModal, setDeleteModal] = useState(null); // client object
  const [deleteInfo, setDeleteInfo] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteReasonDetail, setDeleteReasonDetail] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  const openDeleteModal = async (client) => {
    setDeleteModal(client);
    setDeleteReason(''); setDeleteReasonDetail(''); setDeleteConfirm('');
    try {
      const r = await adminAPI.deletionInfo(client.id);
      setDeleteInfo(r.data);
    } catch {}
  };

  const handleDelete = async () => {
    if (!deleteModal || deleteConfirm !== 'EXCLUIR') return;
    setDeleting(true);
    try {
      await adminAPI.deleteClient(deleteModal.id, { reason: deleteReason, reasonDetail: deleteReasonDetail, confirmation: 'EXCLUIR' });
      toast.success(`Cliente excluido com sucesso`);
      setDeleteModal(null); setDeleteInfo(null);
      fetchClients();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao excluir'); }
    finally { setDeleting(false); }
  };

  return (
    <div className="space-y-5" data-testid="admin-clients">
      {/* Delete Modal — 3 Layer Security */}
      {deleteModal && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={() => setDeleteModal(null)}>
          <div className="bg-[#111817] border border-red-500/20 rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="p-5 border-b border-red-500/15 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center"><AlertTriangle size={20} className="text-red-400"/></div>
              <div>
                <h3 className="font-display text-base font-semibold text-red-400">Excluir Cliente</h3>
                <p className="text-[10px] text-white/30">Acao critica — leia com atencao</p>
              </div>
              <button onClick={() => setDeleteModal(null)} className="ml-auto p-1.5 rounded-lg hover:bg-white/10"><X size={16}/></button>
            </div>

            <div className="p-5 space-y-4">
              {/* Layer 1: Context */}
              <div className="p-3 rounded-xl bg-white/[0.03] border border-white/10 space-y-1">
                <p className="text-sm font-medium text-white/85">{deleteModal.name}</p>
                <p className="text-xs font-mono text-white/50">{deleteModal.cpf || deleteInfo?.cpf || '—'}</p>
                <p className="text-[10px] text-white/30">{deleteModal.email}</p>
                {deleteInfo?.dependencies && (
                  <div className="mt-2 pt-2 border-t border-white/10">
                    <p className="text-[10px] text-white/40 mb-1">Vinculos encontrados:</p>
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(deleteInfo.dependencies).map(([k, v]) => v > 0 && (
                        <span key={k} className="badge-warning text-[8px]">{v} {k}</span>
                      ))}
                      {deleteInfo.totalDependencies === 0 && <span className="text-[9px] text-white/25">Nenhum vinculo</span>}
                    </div>
                  </div>
                )}
              </div>

              {/* Layer 2: Reason */}
              <div>
                <label className="text-xs text-white/40 block mb-1">Motivo da exclusao *</label>
                <select value={deleteReason} onChange={e => setDeleteReason(e.target.value)}
                  className="w-full bg-black/20 border border-white/10 text-white text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:ring-1 focus:ring-red-500/30">
                  <option value="">Selecione o motivo</option>
                  <option value="cadastro_duplicado">Cadastro duplicado</option>
                  <option value="cadastro_incorreto">Cadastro incorreto</option>
                  <option value="solicitacao_interna">Solicitacao interna</option>
                  <option value="teste">Teste</option>
                  <option value="outro">Outro</option>
                </select>
                {deleteReason === 'outro' && (
                  <textarea value={deleteReasonDetail} onChange={e => setDeleteReasonDetail(e.target.value)} placeholder="Descreva o motivo..." rows={2}
                    className="w-full mt-2 bg-black/20 border border-white/10 text-white text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:ring-1 focus:ring-red-500/30"/>
                )}
              </div>

              {/* Layer 3: Type confirmation */}
              <div>
                <label className="text-xs text-white/40 block mb-1">Digite <span className="text-red-400 font-bold">EXCLUIR</span> para confirmar</label>
                <input value={deleteConfirm} onChange={e => setDeleteConfirm(e.target.value)} placeholder="EXCLUIR"
                  className="w-full bg-black/20 border border-red-500/20 text-white text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:ring-1 focus:ring-red-500/30 font-mono"/>
              </div>

              {/* Action */}
              <button onClick={handleDelete}
                disabled={deleting || deleteConfirm !== 'EXCLUIR' || !deleteReason || (deleteReason === 'outro' && !deleteReasonDetail.trim())}
                className="w-full py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                style={{background:'rgba(211,47,47,0.15)', color:'#EF9A9A', border:'1px solid rgba(211,47,47,0.3)'}}>
                {deleting ? <span className="rn-spinner" style={{width:14,height:14}}/> : <><Trash2 size={14}/> Confirmar Exclusao</>}
              </button>
              <p className="text-[9px] text-white/20 text-center">O cliente sera movido para a lixeira e pode ser restaurado.</p>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewUrl && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center" onClick={closePreview}>
          <div className="relative w-full max-w-3xl h-[80vh] bg-[#0B0F10] rounded-2xl overflow-hidden border border-white/10" onClick={e=>e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h3 className="font-display text-sm font-semibold">{previewTitle}</h3>
              <button onClick={closePreview} className="p-1.5 rounded-lg hover:bg-white/10"><X size={16}/></button>
            </div>
            <iframe src={previewUrl} className="w-full h-full" title="preview" />
          </div>
        </div>
      )}

      {/* Hidden serasa input */}
      <input ref={serasaRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={e => {
        if (e.target.files[0] && serasaTarget) handleSerasaUpload(serasaTarget, e.target.files[0]);
        e.target.value = '';
      }} />

      {/* Protocolo Modal */}
      {protoModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center" onClick={()=>setProtoModal(null)}>
          <div className="bg-[#111817] border border-white/10 rounded-2xl w-full max-w-md p-6" onClick={e=>e.stopPropagation()}>
            <h3 className="font-display text-lg font-semibold mb-4">
              {protoModal === 'lote' ? `Protocolar ${selected.length} clientes em Lote` : `Protocolar: ${protoModal.nome}`}
            </h3>
            <div className="space-y-3">
              <input value={protoForm.numeroProtocolo} onChange={e=>setProtoForm(p=>({...p,numeroProtocolo:e.target.value}))} placeholder="Numero do protocolo *"
                className="w-full bg-black/20 border border-white/10 text-white text-sm placeholder:text-white/30 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[rgba(99,203,182,0.3)]"/>
              <textarea value={protoForm.observacao} onChange={e=>setProtoForm(p=>({...p,observacao:e.target.value}))} placeholder="Observacao (opcional)" rows={2}
                className="w-full bg-black/20 border border-white/10 text-white text-sm placeholder:text-white/30 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[rgba(99,203,182,0.3)]"/>
              <div className="flex gap-3">
                <button onClick={()=>setProtoModal(null)} className="btn-ghost flex-1 text-sm">Cancelar</button>
                <button onClick={protoModal === 'lote' ? handleProtocolarLote : handleProtocolarIndividual} disabled={protoLoading || !protoForm.numeroProtocolo}
                  className="btn-primary flex-1 text-sm disabled:opacity-50">
                  {protoLoading ? <span className="rn-spinner mx-auto" style={{width:14,height:14}}/> : 'Protocolar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Baixa Modal */}
      {baixaModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center" onClick={()=>setBaixaModal(null)}>
          <div className="bg-[#111817] border border-white/10 rounded-2xl w-full max-w-md p-6" onClick={e=>e.stopPropagation()}>
            <h3 className="font-display text-lg font-semibold mb-4">
              {baixaModal === 'lote' ? `Baixa em Lote (${selected.length} clientes)` : `Baixa: ${baixaModal.nome}`}
            </h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-white/40 block mb-1">Empresa *</label>
                <select value={baixaForm.empresa} onChange={e=>setBaixaForm(p=>({...p,empresa:e.target.value}))}
                  className="w-full bg-black/20 border border-white/10 text-white text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[rgba(99,203,182,0.3)]">
                  {EMPRESAS.map(e=><option key={e} value={e}>{e.replace('_',' ')}</option>)}
                </select>
              </div>
              <textarea value={baixaForm.observacao} onChange={e=>setBaixaForm(p=>({...p,observacao:e.target.value}))} placeholder="Observacao (opcional)" rows={2}
                className="w-full bg-black/20 border border-white/10 text-white text-sm placeholder:text-white/30 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[rgba(99,203,182,0.3)]"/>
              <div className="flex gap-3">
                <button onClick={()=>setBaixaModal(null)} className="btn-ghost flex-1 text-sm">Cancelar</button>
                <button onClick={baixaModal === 'lote' ? handleBaixaLote : handleBaixaIndividual} disabled={baixaLoading}
                  className="btn-primary flex-1 text-sm disabled:opacity-50">
                  {baixaLoading ? <span className="rn-spinner mx-auto" style={{width:14,height:14}}/> : 'Registrar Baixa'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold">Clientes</h1>
          <p className="text-sm text-white/50">{total} cliente(s)</p>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <button className="btn-primary flex items-center gap-2 text-sm"><Plus size={16}/> Novo</button>
          </DialogTrigger>
          <DialogContent className="bg-[#111817] border-white/10 text-white">
            <DialogHeader><DialogTitle className="font-display">Novo Cliente</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-3 mt-3">
              <input type="text" value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} required placeholder="Nome" className="w-full bg-black/20 border border-white/10 text-white placeholder:text-white/30 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[rgba(99,203,182,0.3)]"/>
              <input type="email" value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))} required placeholder="Email" className="w-full bg-black/20 border border-white/10 text-white placeholder:text-white/30 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[rgba(99,203,182,0.3)]"/>
              <div className="relative">
                <input type={showPw?"text":"password"} value={form.password} onChange={e=>setForm(p=>({...p,password:e.target.value}))} required minLength={6} placeholder="Senha" className="w-full bg-black/20 border border-white/10 text-white placeholder:text-white/30 rounded-xl px-4 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-[rgba(99,203,182,0.3)]"/>
                <button type="button" onClick={()=>setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30" tabIndex={-1}>{showPw?<EyeOff size={14}/>:<Eye size={14}/>}</button>
              </div>
              <input type="tel" value={form.phone} onChange={e=>setForm(p=>({...p,phone:e.target.value}))} required placeholder="WhatsApp" className="w-full bg-black/20 border border-white/10 text-white placeholder:text-white/30 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[rgba(99,203,182,0.3)]"/>
              <button type="submit" disabled={creating} className="w-full btn-primary py-2.5 text-sm disabled:opacity-50">{creating?<span className="rn-spinner mx-auto"/>:'Criar'}</button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[180px] relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30"/>
          <input value={search} onChange={e=>{setSearch(e.target.value);setPage(1);setSelected([]);}} placeholder="Buscar..." className="w-full bg-black/20 border border-white/10 text-white text-sm placeholder:text-white/30 rounded-xl pl-10 pr-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[rgba(99,203,182,0.3)]"/>
        </div>
        <Sel label="Prontos" value={readyFilter} onChange={v=>{setReadyFilter(v);setPage(1);setSelected([]);}} opts={[['','Todos'],['true','Prontos'],['false','Nao prontos']]}/>
        <Sel label="Status" value={statusFilter} onChange={v=>{setStatusFilter(v);setPage(1);setSelected([]);}} opts={[['','Todos'],['PENDENTE','Pendente'],['PROTOCOLAR','Protocolar'],['ENVIADO','Enviado'],['PROTOCOLADO','Protocolado'],['BAIXA_PARCIAL','Baixa Parcial'],['FINALIZADO','Finalizado']]}/>
      </div>

      {/* Selection bar */}
      {selected.length > 0 && (
        <div className="flex items-center gap-3 p-3 rounded-xl" style={{background:'rgba(99,203,182,0.06)',border:'1px solid rgba(99,203,182,0.2)'}}>
          <span className="text-sm font-medium" style={{color:'var(--rn-green)'}}>{selected.length} selecionado(s)</span>
          <div className="flex-1"/>
          <button onClick={handleGenerateList} className="btn-secondary text-xs py-2 px-4 flex items-center gap-1.5"><List size={13}/> Gerar Lista</button>
          <button onClick={()=>setProtoModal('lote')} className="btn-secondary text-xs py-2 px-4 flex items-center gap-1.5">Protocolar Lote</button>
          <button onClick={()=>setBaixaModal('lote')} className="btn-secondary text-xs py-2 px-4 flex items-center gap-1.5">Baixa Lote</button>
          <button onClick={handleExport} disabled={exporting} className="btn-primary text-xs py-2 px-4 flex items-center gap-1.5">{exporting?<span className="rn-spinner" style={{width:12,height:12}}/>:<><Download size={13}/> XLSX</>}</button>
          <button onClick={()=>setSelected([])} className="btn-ghost text-xs py-2 px-3">Limpar</button>
        </div>
      )}

      {/* Table */}
      <div className="glass-card !p-0 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-10"><div className="rn-spinner" style={{width:24,height:24}}/></div>
        ) : clients.length === 0 ? (
          <div className="text-center py-10"><FileText size={32} className="mx-auto text-white/15 mb-2"/><p className="text-sm text-white/35">Nenhum cliente.</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1200px] text-sm" data-testid="admin-clients-table">
              <thead>
                <tr className="border-b border-white/10 bg-white/[0.02]">
                  <th className="py-2.5 px-2 w-8"><Checkbox checked={selected.length===clients.length&&clients.length>0} onCheckedChange={toggleAll} className="border-white/20 data-[state=checked]:bg-[var(--rn-green)] data-[state=checked]:border-[var(--rn-green)]"/></th>
                  <th className="text-left text-[10px] text-white/40 font-medium py-2.5 px-2">NOME</th>
                  <th className="text-left text-[10px] text-white/40 font-medium py-2.5 px-2">CPF/CNPJ</th>
                  <th className="text-center text-[10px] text-white/40 font-medium py-2.5 px-2">DOC</th>
                  <th className="text-center text-[10px] text-white/40 font-medium py-2.5 px-2">TERMO</th>
                  <th className="text-center text-[10px] text-white/40 font-medium py-2.5 px-2">STATUS</th>
                  <th className="text-left text-[10px] text-white/40 font-medium py-2.5 px-2">PENDENCIAS</th>
                  <th className="text-center text-[10px] text-white/40 font-medium py-2.5 px-2">SERASA</th>
                  <th className="text-center text-[10px] text-white/40 font-medium py-2.5 px-2">ACOES</th>
                </tr>
              </thead>
              <tbody>
                {clients.map(c => (
                  <tr key={c.id} className={`border-b border-white/5 transition-colors ${selected.includes(c.id)?'bg-[rgba(99,203,182,0.04)]':'hover:bg-white/[0.02]'}`}>
                    <td className="py-2.5 px-2"><Checkbox checked={selected.includes(c.id)} onCheckedChange={()=>toggleSelect(c.id)} className="border-white/20 data-[state=checked]:bg-[var(--rn-green)] data-[state=checked]:border-[var(--rn-green)]"/></td>
                    <td className="py-2.5 px-2">
                      <p className="text-xs font-medium text-white/90 truncate max-w-[180px]">{c.name}</p>
                      <p className="text-[9px] text-white/30 truncate">{c.email}</p>
                    </td>
                    <td className="py-2.5 px-2"><span className="text-xs font-mono text-white/60">{c.cpf||'—'}</span></td>
                    {/* DOC */}
                    <td className="py-2.5 px-2 text-center">
                      {c.docId ? (
                        <button onClick={()=>openPreview(documentsAPI.getFileUrl(c.docId),`DOC ${c.cpf}`)} className="text-[10px] px-2 py-1 rounded-lg hover:bg-white/5 flex items-center gap-1 mx-auto" style={{color:'var(--rn-green)'}}>
                          <Eye size={12}/> Ver
                        </button>
                      ) : <span className="text-[10px] text-white/25">—</span>}
                    </td>
                    {/* TERMO */}
                    <td className="py-2.5 px-2 text-center">
                      {c.termStatus==='ASSINADO' && c.caseId ? (
                        <button onClick={()=>openPreview(`${BACKEND_URL}/api/cases/${c.caseId}/term/pdf`,`TERMO ${c.cpf}`)} className="text-[10px] px-2 py-1 rounded-lg hover:bg-white/5 flex items-center gap-1 mx-auto" style={{color:'var(--rn-green)'}}>
                          <Eye size={12}/> Ver
                        </button>
                      ) : <span className="text-[10px] text-white/25">Pendente</span>}
                    </td>
                    {/* STATUS */}
                    <td className="py-2.5 px-2 text-center">
                      <span className={`${STATUS_BADGE[c.clientStatus]||'badge-neutral'} text-[10px]`}>{STATUS_LABEL[c.clientStatus]||c.clientStatus}</span>
                    </td>
                    {/* PENDENCIAS */}
                    <td className="py-2.5 px-2">
                      {c.pendencias?.length > 0 ? (
                        <div className="flex flex-wrap gap-1">{c.pendencias.map((p,i)=><span key={i} className="text-[9px] text-white/40 bg-white/[0.04] px-1.5 py-0.5 rounded">{p}</span>)}</div>
                      ) : <span className="text-[10px] text-white/25">—</span>}
                    </td>
                    {/* SERASA */}
                    <td className="py-2.5 px-2 text-center">
                      {c.serasaId ? (
                        <button onClick={()=>openPreview(adminAPI.getSerasaFileUrl(c.serasaId),`SERASA ${c.cpf}`)} className="text-[10px] px-2 py-1 rounded-lg hover:bg-white/5 flex items-center gap-1 mx-auto" style={{color:'var(--rn-green)'}}>
                          <Eye size={12}/> Ver
                        </button>
                      ) : (
                        <button onClick={()=>{setSerasaTarget(c.id);serasaRef.current?.click();}} className="text-[10px] px-2 py-1 rounded-lg hover:bg-white/5 flex items-center gap-1 mx-auto text-white/40">
                          <Upload size={12}/> Anexar
                        </button>
                      )}
                    </td>
                    {/* ACOES */}
                    <td className="py-2.5 px-2 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {c.clientStatus !== 'PROTOCOLADO' && c.clientStatus !== 'BAIXA_PARCIAL' && c.clientStatus !== 'FINALIZADO' && c.caseId && (
                          <button onClick={()=>setProtoModal({caseId:c.caseId,nome:c.name})} className="text-[9px] px-2 py-1 rounded-lg hover:bg-white/5 text-white/50" title="Protocolar">Prot</button>
                        )}
                        {(c.clientStatus === 'PROTOCOLADO' || c.clientStatus === 'BAIXA_PARCIAL') && c.caseId && (
                          <button onClick={()=>setBaixaModal({caseId:c.caseId,nome:c.name})} className="text-[9px] px-2 py-1 rounded-lg hover:bg-white/5" style={{color:'var(--rn-green)'}} title="Baixa">Baixa</button>
                        )}
                        {c.caseId && (
                          <button onClick={()=>navigate(`/admin/cases/${c.caseId}`)} className="text-[9px] px-1.5 py-1 rounded-lg hover:bg-white/5 text-white/30" title="Detalhes">Det</button>
                        )}
                        <button onClick={() => openDeleteModal(c)} className="text-[9px] px-1.5 py-1 rounded-lg hover:bg-red-500/10 text-red-400/50 hover:text-red-400" title="Excluir">
                          <Trash2 size={11}/>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-white/10">
            <span className="text-xs text-white/35">Pag {page}/{pages}</span>
            <div className="flex gap-2">
              <button onClick={()=>{setPage(p=>Math.max(1,p-1));setSelected([]);}} disabled={page===1} className="btn-ghost text-xs px-3 py-1 disabled:opacity-30">Ant</button>
              <button onClick={()=>{setPage(p=>Math.min(pages,p+1));setSelected([]);}} disabled={page===pages} className="btn-ghost text-xs px-3 py-1 disabled:opacity-30">Prox</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Sel({label,value,onChange,opts}) {
  return (
    <div className="min-w-[110px]">
      <label className="text-[10px] text-white/30 block mb-1">{label}</label>
      <select value={value} onChange={e=>onChange(e.target.value)} className="w-full bg-black/20 border border-white/10 text-white text-xs rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[rgba(99,203,182,0.3)]">
        {opts.map(([v,l])=><option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
